/**
 * Product-analytics abstraction. Mirrors the auth / places / weather provider
 * pattern so call-sites stay vendor-neutral and dev / CI run silent without a
 * token. See ADR-0063 for the why.
 *
 * Two concrete adapters exist today:
 *   - `NoopAnalytics`       — used when no provider is configured.
 *   - `MixpanelAnalytics`   — lazy-loads `mixpanel-react-native` (native) or
 *                             `mixpanel-browser` (web). Missing SDK → noop.
 *
 * Call-sites should import the module-level helpers from `./index`
 * (`track`, `identify`, `screen`, `reset`, `flush`) — never instantiate an
 * adapter directly. Test code can swap the active adapter via
 * `_setAnalyticsForTests(...)`.
 */

export type AnalyticsTraits = Record<
  string,
  string | number | boolean | null | undefined
>;

export type AnalyticsProperties = Record<string, unknown>;

export interface AnalyticsAdapter {
  /** Called once during app startup. Idempotent. */
  init(): Promise<void>;
  /** Bind subsequent events to a user. Re-identifying with a different id
   *  re-aliases — the underlying SDK handles distinct-id merging. */
  identify(userId: string, traits?: AnalyticsTraits | undefined): void;
  /** Emit an event. `userId` is optional and ignored on mobile (current
   *  identity is taken from the last `identify`). Present in the shape so
   *  the API can reuse the same interface — see `apps/api/src/lib/analytics-provider.ts`. */
  track(
    event: string,
    properties?: AnalyticsProperties | undefined,
    userId?: string | null | undefined,
  ): void;
  /** Screen / route view. Native SDKs distinguish `screen` from `track`;
   *  web SDKs collapse it onto `track('$pageview')`. The adapter handles
   *  the split. */
  screen(name: string, properties?: AnalyticsProperties | undefined): void;
  /** Route an exception through the provider's error-tracking pipeline.
   *  Mobile error reporting shares the analytics connection (ADR-0065)
   *  rather than running a second SDK; `observability.reportError` is the
   *  public call site. Gated by the same opt-state as `track()` — error
   *  capture follows the `analytics` consent purpose (ADR-0065). */
  captureException(
    error: unknown,
    properties?: AnalyticsProperties | undefined,
  ): void;
  /** Clear the bound user — call on sign-out so subsequent events are
   *  anonymous. */
  reset(): void;
  /** Force-flush the SDK's outbound queue. Best-effort. */
  flush(): Promise<void>;
  /** Resume event capture — see ADR-0065. Called from the consent
   *  bridge when the user opts into analytics. Idempotent. */
  optIn(): void;
  /** Suspend event capture — see ADR-0065. Drops queued events at the
   *  adapter boundary AND calls through to the underlying SDK's
   *  opt-out so any events already inside the SDK pipeline are
   *  dropped before the next network hop. Idempotent. */
  optOut(): void;
  /** True when `optOut()` is the last decision the adapter has seen.
   *  Façade callers (`isAnalyticsOptedOut`) use this; consent UI reads
   *  the user's actual decision from the consent store. */
  isOptedOut(): boolean;
}

/**
 * Used when no analytics provider is configured (no token / unsupported env).
 * Every method is a no-op so dev environments and unit tests run silently.
 */
export class NoopAnalytics implements AnalyticsAdapter {
  async init(): Promise<void> {
    // intentional no-op
  }
  identify(_userId: string, _traits?: AnalyticsTraits | undefined): void {
    // intentional no-op
  }
  track(
    _event: string,
    _properties?: AnalyticsProperties | undefined,
    _userId?: string | null | undefined,
  ): void {
    // intentional no-op
  }
  screen(_name: string, _properties?: AnalyticsProperties | undefined): void {
    // intentional no-op
  }
  captureException(
    _error: unknown,
    _properties?: AnalyticsProperties | undefined,
  ): void {
    // intentional no-op
  }
  reset(): void {
    // intentional no-op
  }
  async flush(): Promise<void> {
    // intentional no-op
  }
  optIn(): void {
    // intentional no-op — Noop never captures regardless.
  }
  optOut(): void {
    // intentional no-op — Noop never captures regardless.
  }
  isOptedOut(): boolean {
    // Noop never captures, so report opted-out for callers that surface
    // "is analytics on?" UI affordances. This is distinct from the
    // user's consent decision (which lives in the consent store).
    return true;
  }
}
